import { defineMessages, IntlShape, MessageDescriptor } from "react-intl";
import { capitalizeFirstLetter } from "@app/utils";
import { TestName, TestType } from "@app/postBill/models";

const testNameMessages = defineMessages({
  billingTestCardHereditaryCancerTest: {
    id: "billingTestCardHereditaryCancerTest",
    defaultMessage: "Hereditary Cancer Test",
  },
});

// TestType (BE shortName) -> localized full-name MessageDescriptor.
// Only add entries for test types whose displayed full name needs to be
// localized differently from the BE-provided `fullName`.
export const testTypeToFullNameMessageMap: Partial<
  Record<TestType, MessageDescriptor>
> = {
  [TestType.EMPOWER]: testNameMessages.billingTestCardHereditaryCancerTest,
};

// BE full-name string -> TestType. Used when we only have a full-name
// string and need to derive the canonical short identifier. Entries here
// take precedence over the "capitalized first word" fallback.
const fullNameToTestTypeMap: Record<string, TestType> = {
  "Hereditary Cancer Test": TestType.EMPOWER,
  "Empower Hereditary Cancer Test": TestType.EMPOWER,
};

export const getTestShortName = (input: TestName | string): string => {
  if (!input) return "";

  if (typeof input !== "string") {
    return input.shortName ?? "";
  }

  const trimmed = input.trim();
  if (!trimmed) return "";

  if (fullNameToTestTypeMap[trimmed]) {
    return fullNameToTestTypeMap[trimmed];
  }

  return capitalizeFirstLetter(trimmed.split(" ")[0]) as string;
};

export const getLocalizedTestFullName = (
  testName: TestName,
  intl: IntlShape
): string => {
  if (!testName) return "";

  const message = testTypeToFullNameMessageMap[testName.shortName as TestType];

  // Only localize when the BE-provided fullName matches the canonical
  // English default for the mapped TestType. This lets callers whose BE
  // returns a different fullName variant (e.g. UPS' "Empower Hereditary
  // Cancer Test") display it verbatim while post-bill data (whose
  // fullName equals the canonical default) is properly translated.
  if (message && testName.fullName === message.defaultMessage) {
    return intl.formatMessage(message);
  }

  return testName.fullName;
};
